import React from "react";
import { Box, Checkbox, Stack, Typography } from "@mui/material";
import { useDataSources } from "../../../api/dataSources";
import { DataSource } from "../../../types/dataSource";

const TEAL = "#00d4ad";
const checkboxSx = { color: "#cbd5e1", "&.Mui-checked": { color: TEAL } };

/** Checkbox list of the organisation's property-boundary data sources
 *  (cadastre / parcel layers). Any number can be chosen: each is pulled over
 *  the project area when the project is created and its polygons become the
 *  project's properties, alongside any uploaded from a file. Not filtered by
 *  location — the single wizard asks for boundaries before the location is
 *  chosen, and the backend clips every source to the project boundary. */
const PropertySourcePicker: React.FC<{
  selected: DataSource[];
  onChange: (selected: DataSource[]) => void;
}> = ({ selected, onChange }) => {
  const { dataSources, isLoading } = useDataSources("property_boundary");

  const toggle = (source: DataSource) => {
    const isSelected = selected.some((s) => s.id === source.id);
    onChange(isSelected ? selected.filter((s) => s.id !== source.id) : [...selected, source]);
  };

  if (isLoading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading sources…
      </Typography>
    );
  }
  if (dataSources.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No property boundary data sources are configured. Add one under Property Boundaries
        in the Data Management console, or upload a file instead.
      </Typography>
    );
  }

  return (
    <Stack>
      {dataSources.map((source) => {
        const isSelected = selected.some((s) => s.id === source.id);
        const filterCount = Array.isArray(source.config?.filters)
          ? (source.config.filters as unknown[]).length
          : 0;
        return (
          <Box
            key={source.id}
            onClick={() => toggle(source)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              py: 1,
              borderRadius: 1.5,
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f8fafc" },
            }}
          >
            <Checkbox checked={isSelected} sx={checkboxSx} disableRipple />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {source.name}
              </Typography>
              {filterCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {filterCount} attribute filter{filterCount === 1 ? "" : "s"}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default PropertySourcePicker;
